import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Badge, Button, Card, CardContent, Input, Label } from '@caratuva/ui';
import { Section, SectionHeading } from '../components/Section.tsx';
import { GETPAID_SIGNUP_URL } from '../lib/links.ts';
import { estimateMonthlyEarnings } from '../lib/affiliate-tiers.ts';
import { useRouteMeta } from '../lib/useDocumentMeta.ts';

export const Route = createFileRoute('/affiliates')({
  component: AffiliatesPage,
});

const DEFAULT_CALCULATOR_VOLUME_USD = 250_000;
const CALCULATOR_MIN_USD = 0;
const CALCULATOR_MAX_USD = 20_000_000;
const CALCULATOR_STEP_USD = 10_000;

function HowStep({ index, titleId, bodyId }: { index: number; titleId: string; bodyId: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-navy-950)] text-sm font-semibold text-[var(--color-beige-50)]">
          {index}
        </div>
        <h3 className="mt-4 text-base font-semibold text-[var(--color-navy-950)]">
          <FormattedMessage id={titleId} />
        </h3>
        <p className="mt-2 text-sm text-[var(--color-navy-700)]">
          <FormattedMessage id={bodyId} />
        </p>
      </CardContent>
    </Card>
  );
}

function TierTable() {
  const rows = [
    { volume: 'affiliates.tiers.row1.volume', share: 'affiliates.tiers.row1.share' },
    { volume: 'affiliates.tiers.row2.volume', share: 'affiliates.tiers.row2.share' },
    { volume: 'affiliates.tiers.row3.volume', share: 'affiliates.tiers.row3.share' },
    { volume: 'affiliates.tiers.row4.volume', share: 'affiliates.tiers.row4.share' },
  ];
  return (
    <div className="mx-auto mt-6 max-w-xl overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-beige-300)]">
            <th className="py-3 pr-4 font-medium text-[var(--color-navy-500)]">
              <FormattedMessage id="affiliates.tiers.col.volume" />
            </th>
            <th className="py-3 text-right font-semibold text-[var(--color-navy-950)]">
              <FormattedMessage id="affiliates.tiers.col.share" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.volume} className="border-b border-[var(--color-beige-200)]">
              <td className="py-3 pr-4 font-medium text-[var(--color-navy-800)]">
                <FormattedMessage id={r.volume} />
              </td>
              <td className="py-3 text-right font-semibold text-[var(--color-navy-950)]">
                <FormattedMessage id={r.share} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EarningsCalculator() {
  const intl = useIntl();
  const [volumeUsd, setVolumeUsd] = useState(DEFAULT_CALCULATOR_VOLUME_USD);

  const estimate = useMemo(() => estimateMonthlyEarnings(volumeUsd), [volumeUsd]);

  const handleVolumeChange = (raw: string) => {
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {
      setVolumeUsd(0);
      return;
    }
    setVolumeUsd(Math.min(Math.max(parsed, CALCULATOR_MIN_USD), CALCULATOR_MAX_USD));
  };

  return (
    <Card className="border-[var(--color-navy-300)]">
      <CardContent className="pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-navy-600)]">
          <FormattedMessage id="affiliates.calculator.title" />
        </h3>
        <p className="mt-2 text-sm text-[var(--color-navy-700)]">
          <FormattedMessage id="affiliates.calculator.subtitle" />
        </p>

        <div className="mt-6">
          <Label htmlFor="affiliate-volume">
            <FormattedMessage id="affiliates.calculator.volumeLabel" />
          </Label>
          <Input
            id="affiliate-volume"
            type="number"
            inputMode="numeric"
            min={CALCULATOR_MIN_USD}
            max={CALCULATOR_MAX_USD}
            step={CALCULATOR_STEP_USD}
            value={volumeUsd}
            onChange={(e) => handleVolumeChange(e.target.value)}
            className="mt-2"
          />
          <input
            type="range"
            aria-label={intl.formatMessage({ id: 'affiliates.calculator.volumeLabel' })}
            min={CALCULATOR_MIN_USD}
            max={CALCULATOR_MAX_USD}
            step={CALCULATOR_STEP_USD}
            value={volumeUsd}
            onChange={(e) => handleVolumeChange(e.target.value)}
            className="mt-4 w-full accent-[var(--color-navy-950)]"
          />
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
              <FormattedMessage id="affiliates.calculator.result.fee" />
            </p>
            <p className="mt-1 text-2xl font-semibold text-[var(--color-navy-950)]">
              <FormattedNumber
                value={estimate.caratuvaFeeBps / 10_000}
                style="percent"
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
              <FormattedMessage id="affiliates.calculator.result.share" />
            </p>
            <p className="mt-1 text-2xl font-semibold text-[var(--color-navy-950)]">
              <FormattedNumber
                value={estimate.shareBps / 10_000}
                style="percent"
                minimumFractionDigits={0}
                maximumFractionDigits={2}
              />
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
              <FormattedMessage id="affiliates.calculator.result.earnings" />
            </p>
            <p className="mt-1 text-2xl font-semibold text-[var(--color-navy-950)]">
              <FormattedNumber
                value={estimate.monthlyEarningsUsd}
                style="currency"
                currency="USD"
                maximumFractionDigits={0}
              />
              <span className="text-sm font-normal text-[var(--color-navy-500)]">
                {' '}
                <FormattedMessage id="affiliates.calculator.result.perMonth" />
              </span>
            </p>
          </div>
        </div>

        <p className="mt-6 text-xs text-[var(--color-navy-500)]">
          <FormattedMessage id="affiliates.calculator.disclaimer" />
        </p>
      </CardContent>
    </Card>
  );
}

function AffiliatesPage() {
  useRouteMeta('meta.affiliates.title', 'meta.affiliates.description', 'meta.affiliates.social');
  const intl = useIntl();
  return (
    <>
      <Section tint="white" className="pt-20 sm:pt-24">
        <SectionHeading
          eyebrow={<FormattedMessage id="affiliates.eyebrow" />}
          title={<FormattedMessage id="affiliates.title" />}
          subtitle={<FormattedMessage id="affiliates.subtitle" />}
        />
        <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-3">
          <HowStep
            index={1}
            titleId="affiliates.how.step1.title"
            bodyId="affiliates.how.step1.body"
          />
          <HowStep
            index={2}
            titleId="affiliates.how.step2.title"
            bodyId="affiliates.how.step2.body"
          />
          <HowStep
            index={3}
            titleId="affiliates.how.step3.title"
            bodyId="affiliates.how.step3.body"
          />
        </div>
        <div className="mx-auto mt-6 max-w-4xl">
          <Card className="border-[var(--color-navy-300)] bg-[rgba(0,168,104,0.06)]">
            <CardContent className="pt-6 text-center">
              <Badge variant="success">
                <FormattedMessage id="affiliates.perk.title" />
              </Badge>
              <p className="mt-3 text-sm font-medium text-[var(--color-navy-800)]">
                <FormattedMessage id="affiliates.perk.body" />
              </p>
            </CardContent>
          </Card>
        </div>
      </Section>

      <Section tint="canvas">
        <SectionHeading
          title={<FormattedMessage id="affiliates.tiers.title" />}
          subtitle={<FormattedMessage id="affiliates.tiers.subtitle" />}
        />
        <TierTable />
        <p className="mx-auto mt-3 max-w-xl text-center text-xs text-[var(--color-navy-500)]">
          <FormattedMessage id="affiliates.tiers.note" />
        </p>
      </Section>

      <Section tint="white">
        <SectionHeading title={<FormattedMessage id="affiliates.calculator.heading" />} />
        <div className="mx-auto mt-8 max-w-2xl">
          <EarningsCalculator />
        </div>
      </Section>

      <Section tint="brand">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-beige-50)] sm:text-4xl">
            <FormattedMessage id="affiliates.cta.title" />
          </h2>
          <p className="mt-4 text-[var(--color-beige-200)]">
            <FormattedMessage id="affiliates.cta.subtitle" />
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="bg-[var(--color-beige-50)] text-[var(--color-navy-950)] hover:bg-[var(--color-beige-100)]"
            >
              <a href={GETPAID_SIGNUP_URL}>
                {intl.formatMessage({ id: 'affiliates.cta.primary' })}
              </a>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
